import type { IntlShape } from 'react-intl';

import { formatResolution } from '../../../../../core/components/tooltip/utils/resolution-formatter.js';
import type {
  FormatterWithPrecision,
  StringFormatter,
  TimeFrameFormatter,
} from '../../../../../core/types/formatter.js';
import { XYCHART_MESSAGES } from '../../../../messages/messages.js';
import type { XYAccessorDataTypes } from '../../../../types/xy-chart.js';
import {
  isCategoricalTooltip,
  isNumericalToolTip,
  isTimeTooltip,
} from '../../../../utils/type-guards.js';

/**
 * Pure function that formats tooltip body content.
 * This can be called from non-React contexts (like utility functions).
 *
 * `formatter` is discriminated by the value type: a `StringFormatter` for
 * categorical axes, a `FormatterWithPrecision` for numerical axes, and a
 * `TimeFrameFormatter` for time axes.
 */
export function getHoveredPointTooltipBodyContent(
  intl: IntlShape,
  v0?: XYAccessorDataTypes | undefined,
  v1?: XYAccessorDataTypes | undefined,
  formatter?: FormatterWithPrecision | TimeFrameFormatter | StringFormatter,
  toPercentage?: (value: number) => number,
) {
  const separator = intl.formatMessage(XYCHART_MESSAGES.separatorDash);

  let duration: string | undefined;
  let text = '';

  const primaryValue = v0 ?? v1;
  const secondaryValue = v0 !== undefined && v1 !== undefined ? v1 : undefined;

  if (primaryValue === undefined) {
    return { duration, text };
  }

  const args = {
    formatter,
    v0: primaryValue,
    v1: secondaryValue,
  };

  if (isCategoricalTooltip(args)) {
    const format = formatter as StringFormatter | undefined;
    const formatValue = (v: string) => (format ? format(v) : v);
    const startText = formatValue(String(args.v0));
    const endText =
      args.v1 === undefined ? startText : formatValue(String(args.v1));

    text =
      args.v1 === undefined
        ? startText
        : `${startText} ${separator} ${endText}`;

    return { duration, text };
  }

  if (isNumericalToolTip(args) && formatter) {
    const format = formatter as FormatterWithPrecision;
    const percentageSuffix = (v: number) =>
      toPercentage ? ` (${toPercentage(v)}%)` : '';
    const formattedStart = `${format(args.v0 as number)}${percentageSuffix(
      args.v0,
    )}`;
    const formattedEnd =
      args.v1 === undefined
        ? formattedStart
        : `${format(args.v1 as number)}${percentageSuffix(args.v1)}`;

    text =
      args.v1 === undefined
        ? formattedStart
        : `${formattedStart} ${separator} ${formattedEnd}`;

    return { duration, text };
  }

  if (isTimeTooltip(args) && formatter) {
    const format = formatter as TimeFrameFormatter;

    if (args.v0 instanceof Date) {
      text = format(args.v0, args.v1 instanceof Date ? args.v1 : undefined);

      if (args.v1 instanceof Date) {
        duration = formatResolution(args.v0, args.v1);
      }
    }
  }

  return { duration, text };
}
